import { Ban } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@ui/lib/utils';

export interface BlockedAccountBannerTranslations {
  title: string;
  /**
   * Description shown when no admin-provided reason is available.
   */
  fallbackDescription: string;
  /**
   * Prefix used in front of the admin-provided block reason. Example:
   *   `Reason: 3 consecutive cancellations without completed booking`
   */
  reasonLabel: string;
}

export interface BlockedAccountBannerProps {
  translations: BlockedAccountBannerTranslations;
  /**
   * Latest block reason captured when the admin (or system) blocked the
   * account. Sourced from `professional.blockHistory`. When absent the banner
   * falls back to the generic description.
   */
  reason?: string;
  /**
   * Rendered inside the CTA container — typically a mail / support link. The
   * host app wires up the actual href so routing / analytics stay consumer-side.
   */
  ctaSlot?: ReactNode;
  className?: string;
}

/**
 * Persistent site banner shown to a professional whose account has been
 * blocked. Purely presentational: the consumer is responsible for deciding
 * when to render it (typically `user.professionalBlockedAt != null`).
 *
 * Intentionally uses the destructive palette so the status is unmissable —
 * unlike the verification banner which uses warning / info tones.
 */
export function BlockedAccountBanner({ translations, reason, ctaSlot, className }: BlockedAccountBannerProps) {
  const description = reason ? `${translations.reasonLabel}: ${reason}` : translations.fallbackDescription;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn('w-full border-b border-red-200 bg-red-50 py-3 text-red-900', className)}
    >
      <div className="layout-bounds flex flex-row items-center gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="flex-shrink-0 pt-0.5" aria-hidden="true">
            <Ban className="size-5" />
          </span>
          <div className="flex flex-col gap-0.5 min-w-0">
            <span className="text-sm font-semibold leading-tight">{translations.title}</span>
            <span className="text-sm opacity-90 leading-snug break-words">{description}</span>
          </div>
        </div>
        {ctaSlot && <div className="flex-shrink-0 ml-auto">{ctaSlot}</div>}
      </div>
    </div>
  );
}
